import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  sendChatMessage,
  cancelChat,
  getConversations,
  getInProgressConversations,
  createConversation,
  getConversation,
  deleteConversation,
  updateConversation,
  AgentMismatchError,
} from '@/shared/lib/api'
import { eventBus } from '@/shared/lib/eventBus'
import { useOrchestrationStore } from '@/features/orchestration/store/orchestrationStore'
import { useChatStore } from '../store/useChatStore'
import type { ChatMessage, MessageAttachment } from '../types'
import type { ConversationListItem, FunctionCall, AgentInfo, ContentBlock, MessageMeta, AskUserQuestion } from '@/shared/types'
import type { AttachmentFile } from '../components/ChatInput'
import type { GlobalEventEnvelope } from '@/shared/lib/api/sse'

const LS_KEY = 'chat_conversation_id'

interface UseChatOptions {
  /** Pre-select this agent when the hook mounts. If the user later changes
   *  the agent via the dropdown, their choice takes precedence. */
  initialAgentDefinitionId?: number | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-conversation streaming accumulators (module-level, not React state)
// These outlive individual hook instances so background streams work correctly.
// ─────────────────────────────────────────────────────────────────────────────

interface ConversationAccumulator {
  accText: string
  accCalls: FunctionCall[]
  accAgent: AgentInfo | undefined
  accMeta: MessageMeta
  accBlocks: ContentBlock[]
  rafId: number | null
  pendingUpdate: boolean
}

const accumulators = new Map<number, ConversationAccumulator>()

function getOrCreateAccumulator(conversationId: number): ConversationAccumulator {
  if (!accumulators.has(conversationId)) {
    accumulators.set(conversationId, {
      accText: '',
      accCalls: [],
      accAgent: undefined,
      accMeta: {},
      accBlocks: [],
      rafId: null,
      pendingUpdate: false,
    })
  }
  return accumulators.get(conversationId)!
}

function clearAccumulator(conversationId: number): void {
  const acc = accumulators.get(conversationId)
  if (acc?.rafId !== null && acc?.rafId != null) {
    cancelAnimationFrame(acc.rafId)
  }
  accumulators.delete(conversationId)
}

// ─────────────────────────────────────────────────────────────────────────────
// Reconnection helper — restores in-progress sessions after a page refresh
// ─────────────────────────────────────────────────────────────────────────────

/**
 * On app init, fetch conversations where the last message is from the user
 * (i.e. the backend agent is still running). For each one:
 *   1. Load existing messages from DB (user message + history, no assistant yet)
 *   2. Append an empty AI placeholder so dispatchChatEvent's flushUpdate has a
 *      target when the `done` event arrives
 *   3. Mark the conversation as streaming so ring-buffer replayed events are
 *      processed instead of dropped
 *   4. Set a 3-minute safety timeout — if no `done` event arrives (backend
 *      restarted and the task is gone), reload from DB and clear streaming state
 */
async function restoreInProgressConversations(
  addStreamingConversation: (id: number) => void,
  setMessagesForConversation: (id: number, updater: (prev: ChatMessage[]) => ChatMessage[]) => void,
): Promise<void> {
  let inProgress: Awaited<ReturnType<typeof getInProgressConversations>>
  try {
    inProgress = await getInProgressConversations()
  } catch {
    return // non-critical — silently skip if the request fails
  }
  if (inProgress.length === 0) return

  await Promise.all(
    inProgress.map(async (conv) => {
      try {
        const full = await getConversation(conv.id)
        const messages: ChatMessage[] = full.messages
          .filter((msg) => msg.content != null)
          .map((msg) => ({
            sender: msg.role === 'user' ? ('user' as const) : ('ai' as const),
            text: msg.content || '',
            functionCalls: msg.function_calls,
            blocks: msg.content_blocks,
            agent: msg.agent_info,
            timestamp: msg.created_at,
          }))

        // AI placeholder — required for dispatchChatEvent's updateLastAiMessage
        // to have a target when the `done` event fires
        messages.push({ sender: 'ai', text: '', timestamp: new Date().toISOString() })

        setMessagesForConversation(conv.id, () => messages)
        addStreamingConversation(conv.id)

        // Safety timeout: if the backend task is gone (e.g. server restart),
        // no `done` event will arrive. After 3 min, clean up and reload from DB.
        setTimeout(() => {
          const state = useChatStore.getState()
          if (!state.streamingConversationIds.has(conv.id)) return
          state.removeStreamingConversation(conv.id)
          getConversation(conv.id)
            .then((fresh) => {
              const reloaded: ChatMessage[] = fresh.messages
                .filter((m) => m.content != null)
                .map((m) => ({
                  sender: m.role === 'user' ? ('user' as const) : ('ai' as const),
                  text: m.content || '',
                  functionCalls: m.function_calls,
                  blocks: m.content_blocks,
                  agent: m.agent_info,
                  timestamp: m.created_at,
                }))
              setMessagesForConversation(conv.id, () => reloaded)
            })
            .catch(() => {})
        }, 3 * 60 * 1000)
      } catch {
        // Non-critical — silently fail per conversation
      }
    }),
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Global event dispatcher — called by useGlobalEventStream for source='chat'
// ─────────────────────────────────────────────────────────────────────────────

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Validate the SSE envelope's `questions` field into typed AskUserQuestion[].
 * The envelope is untyped (`[key: string]: unknown`), so this checks shape
 * rather than casting — malformed questions are dropped rather than crashing
 * the card, and a fully-empty/invalid result returns undefined so callers
 * fall back to the legacy single-question card.
 */
export function parseAskUserQuestions(raw: unknown): AskUserQuestion[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const parsed: AskUserQuestion[] = []
  for (const item of raw) {
    if (!isPlainObject(item) || typeof item.question !== 'string') continue
    const rawOptions = Array.isArray(item.options) ? item.options : []
    const options = rawOptions
      .filter((o): o is Record<string, unknown> => isPlainObject(o) && typeof o.label === 'string')
      .map((o) => ({
        label: o.label as string,
        description: typeof o.description === 'string' ? o.description : undefined,
      }))
    parsed.push({
      question: item.question,
      header: typeof item.header === 'string' ? item.header : undefined,
      options: options.length > 0 ? options : undefined,
      multiSelect: typeof item.multiSelect === 'boolean' ? item.multiSelect : undefined,
    })
  }
  return parsed.length > 0 ? parsed : undefined
}

export function dispatchChatEvent(envelope: GlobalEventEnvelope): void {
  const conversationId = envelope.source_id as number
  const store = useChatStore.getState()
  const acc = getOrCreateAccumulator(conversationId)

  const updateLastAiMessage = (updater: (msg: ChatMessage) => ChatMessage) => {
    store.setMessagesForConversation(conversationId, (prev) => {
      const updated = [...prev]
      const last = updated[updated.length - 1]
      if (last?.sender === 'ai') {
        updated[updated.length - 1] = updater(last)
      }
      return updated
    })
  }

  const scheduleUpdate = () => {
    if (acc.pendingUpdate) return
    acc.pendingUpdate = true
    acc.rafId = requestAnimationFrame(() => {
      updateLastAiMessage((msg) => ({
        ...msg,
        text: acc.accText,
        functionCalls: acc.accCalls.length > 0 ? acc.accCalls : msg.functionCalls,
        blocks: acc.accBlocks.length > 0 ? acc.accBlocks : msg.blocks,
        agent: acc.accAgent ?? msg.agent,
        meta: Object.keys(acc.accMeta).length > 0 ? acc.accMeta : msg.meta,
      }))
      acc.pendingUpdate = false
      acc.rafId = null
    })
  }

  const flushUpdate = () => {
    if (acc.rafId !== null) {
      cancelAnimationFrame(acc.rafId)
      acc.rafId = null
    }
    acc.pendingUpdate = false
    updateLastAiMessage((msg) => ({
      ...msg,
      text: acc.accText,
      functionCalls: acc.accCalls.length > 0 ? acc.accCalls : msg.functionCalls,
      blocks: acc.accBlocks.length > 0 ? acc.accBlocks : msg.blocks,
      agent: acc.accAgent ?? msg.agent,
      meta: Object.keys(acc.accMeta).length > 0 ? acc.accMeta : msg.meta,
    }))
  }

  switch (envelope.type) {
    case 'text': {
      // Skip replayed events for conversations not currently streaming.
      // This prevents ring-buffer replay (after page load with since=0) from
      // corrupting messages that were already loaded from the database.
      if (!store.streamingConversationIds.has(conversationId)) return
      const content = envelope.content as string
      if (!store.firstTokenReceivedIds.has(conversationId)) {
        store.addFirstTokenReceived(conversationId)
      }
      acc.accText += content
      const lastBlock = acc.accBlocks.length > 0 ? acc.accBlocks[acc.accBlocks.length - 1] : null
      if (lastBlock?.type === 'text') {
        lastBlock.content += content
      } else {
        acc.accBlocks.push({ type: 'text', content })
      }
      scheduleUpdate()
      break
    }

    case 'tool_call': {
      if (!store.streamingConversationIds.has(conversationId)) return
      if (!store.firstTokenReceivedIds.has(conversationId)) {
        store.addFirstTokenReceived(conversationId)
      }
      const call: FunctionCall = {
        name: envelope.name as string,
        args: envelope.args as Record<string, unknown>,
      }
      acc.accCalls.push(call)
      acc.accBlocks.push({
        type: 'tool_call',
        id: envelope.id as string,
        name: envelope.name as string,
        args: envelope.args as Record<string, unknown>,
      })
      scheduleUpdate()
      break
    }

    case 'tool_result': {
      const resultBlock: ContentBlock = {
        type: 'tool_result',
        tool_use_id: envelope.tool_use_id as string,
        content: envelope.content as string,
        is_error: envelope.is_error as boolean,
      }
      const lastSummary = acc.accBlocks.length > 0 ? acc.accBlocks[acc.accBlocks.length - 1] : null
      if (
        lastSummary?.type === 'subagent_summary' &&
        lastSummary.events.some((ev) => ev.type === 'subagent_tool_call' && 'name' in ev)
      ) {
        lastSummary.events.push(resultBlock)
        lastSummary.count = lastSummary.events.length
      } else {
        acc.accBlocks.push(resultBlock)
      }
      scheduleUpdate()
      break
    }

    case 'subagent_text': {
      if (!store.firstTokenReceivedIds.has(conversationId)) {
        store.addFirstTokenReceived(conversationId)
      }
      const subEvent: ContentBlock = { type: 'subagent_text', content: envelope.content as string }
      const existingSummary = acc.accBlocks.length > 0 ? acc.accBlocks[acc.accBlocks.length - 1] : null
      if (existingSummary?.type === 'subagent_summary') {
        existingSummary.events.push(subEvent)
        existingSummary.count = existingSummary.events.length
      } else {
        acc.accBlocks.push({ type: 'subagent_summary', count: 1, currentTool: null, events: [subEvent] })
      }
      scheduleUpdate()
      break
    }

    case 'subagent_tool_call': {
      if (!store.firstTokenReceivedIds.has(conversationId)) {
        store.addFirstTokenReceived(conversationId)
      }
      const subToolEvent: ContentBlock = {
        type: 'subagent_tool_call',
        name: envelope.name as string,
        args: envelope.args as Record<string, unknown>,
      }
      const existingToolSummary = acc.accBlocks.length > 0 ? acc.accBlocks[acc.accBlocks.length - 1] : null
      if (existingToolSummary?.type === 'subagent_summary') {
        existingToolSummary.events.push(subToolEvent)
        existingToolSummary.count = existingToolSummary.events.length
        existingToolSummary.currentTool = envelope.name as string
      } else {
        acc.accBlocks.push({
          type: 'subagent_summary',
          count: 1,
          currentTool: envelope.name as string,
          events: [subToolEvent],
        })
      }
      scheduleUpdate()
      break
    }

    case 'ask_user_input': {
      if (!store.streamingConversationIds.has(conversationId)) return
      if (!store.firstTokenReceivedIds.has(conversationId)) {
        store.addFirstTokenReceived(conversationId)
      }
      const toolUseId = envelope.tool_use_id as string
      const promptBlock: ContentBlock = {
        type: 'user_input_prompt',
        tool_use_id: toolUseId,
        pause_key: (envelope.pause_key as string | undefined) ?? toolUseId,
        question: envelope.question as string,
        questions: parseAskUserQuestions(envelope.questions),
        answered: false,
      }
      acc.accBlocks.push(promptBlock)
      scheduleUpdate()
      break
    }

    case 'done': {
      const chatMessage = envelope.chat_message as string
      const functionCalls = envelope.function_calls as FunctionCall[]
      const contentBlocks = envelope.content_blocks as ContentBlock[] | undefined
      const agent = envelope.agent as AgentInfo
      const newConversationId = envelope.conversation_id as number | null

      acc.accText = chatMessage
      acc.accCalls = functionCalls
      if (contentBlocks && contentBlocks.length >= acc.accBlocks.length) {
        acc.accBlocks = contentBlocks
      }
      acc.accAgent = agent
      acc.accMeta = {
        model: envelope.model as string | undefined,
        totalCostUsd: envelope.total_cost_usd as number | null | undefined,
        usage: envelope.usage as MessageMeta['usage'],
        durationMs: envelope.duration_ms as number | undefined,
        numTurns: envelope.num_turns as number | undefined,
      }

      // Sync conversation_id if backend created a new one
      if (newConversationId && newConversationId !== conversationId) {
        const currentRef = store.currentConversationId
        if (currentRef === null) {
          store.setCurrentConversationId(newConversationId)
        }
        localStorage.setItem(LS_KEY, String(newConversationId))
      } else {
        localStorage.setItem(LS_KEY, String(conversationId))
      }

      store.removeStreamingConversation(conversationId)
      flushUpdate()
      clearAccumulator(conversationId)

      // Reload conversations list so new title/item appears
      getConversations()
        .then((convos) => store.setConversations(convos))
        .catch(() => {})

      // Emit tile-refresh events for tool calls that mutated data
      if (functionCalls.length > 0) {
        const names = functionCalls.map((fc) => fc.name)
        if (names.some((n) => n.includes('task') || n === 'create_task' || n === 'update_task' || n === 'delete_task')) {
          eventBus.emit('tasks:updated')
        }
        if (names.some((n) => n.includes('habit') || n === 'log_habit_completion')) {
          eventBus.emit('habits:updated')
        }
        if (names.some((n) => n.includes('tracking') || n === 'log_tracking_event')) {
          eventBus.emit('tracking:updated')
        }
      }

      store.removeFirstTokenReceived(conversationId)
      break
    }

    case 'cancelled': {
      flushUpdate()
      store.removeStreamingConversation(conversationId)
      store.removeFirstTokenReceived(conversationId)
      clearAccumulator(conversationId)
      break
    }

    case 'error': {
      flushUpdate()
      store.setMessagesForConversation(conversationId, (prev) => {
        const updated = [...prev]
        const last = updated[updated.length - 1]
        if (last?.sender === 'ai') {
          updated[updated.length - 1] = { ...last, text: `Error: ${envelope.message as string}` }
        }
        return updated
      })
      store.removeStreamingConversation(conversationId)
      store.removeFirstTokenReceived(conversationId)
      clearAccumulator(conversationId)
      break
    }

    case 'init': {
      // Backend signals a new conversation was created for this stream
      const initConversationId = envelope.conversation_id as number | null
      if (initConversationId && initConversationId !== conversationId) {
        const currentRef = store.currentConversationId
        if (currentRef === null) {
          store.setCurrentConversationId(initConversationId)
        }
        localStorage.setItem(LS_KEY, String(initConversationId))
      }
      break
    }

    default: {
      if (import.meta.env.DEV) {
        console.warn('[dispatchChatEvent] Unhandled event type:', envelope.type)
      }
      break
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper to mark a user input prompt as answered
// ─────────────────────────────────────────────────────────────────────────────

export function markInputAnswered(conversationId: number, toolUseId: string, answers: Record<string, string>): void {
  const store = useChatStore.getState()
  store.setMessagesForConversation(conversationId, (prev) => {
    return prev.map((msg) => {
      if (msg.sender !== 'ai' || !msg.blocks) return msg
      const updatedBlocks = msg.blocks.map((block) => {
        if (block.type === 'user_input_prompt' && block.tool_use_id === toolUseId) {
          return { ...block, answered: true, answers }
        }
        return block
      })
      return { ...msg, blocks: updatedBlocks }
    })
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// useChat hook
// ─────────────────────────────────────────────────────────────────────────────

export function useChat({ initialAgentDefinitionId }: UseChatOptions = {}) {
  // Read from store
  const messagesMap = useChatStore((state) => state.messagesMap)
  const conversations = useChatStore((state) => state.conversations)
  const currentConversationId = useChatStore((state) => state.currentConversationId)
  const streamingConversationIds = useChatStore((state) => state.streamingConversationIds)
  const firstTokenReceivedIds = useChatStore((state) => state.firstTokenReceivedIds)
  const isLoadingConversations = useChatStore((state) => state.isLoadingConversations)
  const isLoadingMessages = useChatStore((state) => state.isLoadingMessages)
  const conversationsLoaded = useChatStore((state) => state.conversationsLoaded)
  const suggestedAgentDefinitionId = useChatStore((state) => state.suggestedAgentDefinitionId)

  // Store actions
  const setMessagesForConversation = useChatStore((state) => state.setMessagesForConversation)
  const setConversations = useChatStore((state) => state.setConversations)
  const setCurrentConversationId = useChatStore((state) => state.setCurrentConversationId)
  const setIsLoadingConversations = useChatStore((state) => state.setIsLoadingConversations)
  const setIsLoadingMessages = useChatStore((state) => state.setIsLoadingMessages)
  const setConversationsLoaded = useChatStore((state) => state.setConversationsLoaded)
  const addStreamingConversation = useChatStore((state) => state.addStreamingConversation)

  // Local state (per-instance, not shared)
  const [model, setModel] = useState('claude-sonnet-4-6')
  const [agentDefinitionId, setAgentDefinitionId] = useState<number | null>(
    initialAgentDefinitionId ?? suggestedAgentDefinitionId ?? null
  )
  // Tracks whether the user has explicitly changed the agent. If they have,
  // we never override their choice with an updated initialAgentDefinitionId.
  const userSelectedAgentRef = useRef(false)

  // Apply the default agent when it resolves asynchronously, but only if the
  // user has not yet made an explicit agent selection.
  useEffect(() => {
    if (initialAgentDefinitionId != null && !userSelectedAgentRef.current) {
      setAgentDefinitionId(initialAgentDefinitionId)
    }
  }, [initialAgentDefinitionId])

  // Apply suggested agent from store if no explicit selection was made
  useEffect(() => {
    if (suggestedAgentDefinitionId != null && !userSelectedAgentRef.current && initialAgentDefinitionId == null) {
      setAgentDefinitionId(suggestedAgentDefinitionId)
    }
  }, [suggestedAgentDefinitionId, initialAgentDefinitionId])

  // Wrap setAgentDefinitionId so we can track explicit user selections.
  const handleSetAgentDefinitionId = useCallback((id: number | null) => {
    userSelectedAgentRef.current = true
    setAgentDefinitionId(id)
  }, [])

  // Workspace — synced from orchestration store (active workspace)
  const { workspaces, activeWorkspace, fetchWorkspaces: fetchWs } = useOrchestrationStore()
  const [workspaceId, setWorkspaceId] = useState<number | null>(null)

  // On mount, ensure workspaces are loaded and default to the active one
  useEffect(() => {
    fetchWs()
  }, [fetchWs])

  useEffect(() => {
    if (workspaceId === null && activeWorkspace) {
      setWorkspaceId(activeWorkspace.id)
    }
  }, [activeWorkspace, workspaceId])

  // Derive the workspace path for the currently selected workspace
  const selectedWorkspace = workspaces.find((w) => w.id === workspaceId) ?? activeWorkspace
  const workspacePath = selectedWorkspace?.path ?? null

  // Keep a ref to the current conversation ID that is stable inside async callbacks.
  const currentConversationIdRef = useRef<number | null>(null)

  // Derive the messages array for the currently viewed conversation.
  const messages = useMemo(
    () => (currentConversationId != null ? messagesMap.get(currentConversationId) : undefined) ?? [],
    [currentConversationId, messagesMap]
  )

  // Derived state — scoped to the currently viewed conversation only.
  const isLoading = isLoadingMessages || (currentConversationId != null && streamingConversationIds.has(currentConversationId))
  const isStreaming = currentConversationId != null && firstTokenReceivedIds.has(currentConversationId)

  const loadConversations = useCallback(async (): Promise<ConversationListItem[] | undefined> => {
    try {
      setIsLoadingConversations(true)
      const convos = await getConversations()
      setConversations(convos)
      setConversationsLoaded(true)
      return convos
    } catch (error) {
      console.error('Failed to load conversations:', error)
    } finally {
      setIsLoadingConversations(false)
    }
  }, [setIsLoadingConversations, setConversations, setConversationsLoaded])

  // Track previous workspace ID to detect actual changes (not just initial mount)
  const prevWorkspaceIdRef = useRef<number | null | undefined>(undefined)

  // Re-fetch conversations when the active workspace changes
  useEffect(() => {
    const currentWsId = activeWorkspace?.id ?? null

    // Skip if this is the first render (ref is still undefined)
    if (prevWorkspaceIdRef.current === undefined) {
      prevWorkspaceIdRef.current = currentWsId
      return
    }

    // Skip if workspace hasn't actually changed
    if (prevWorkspaceIdRef.current === currentWsId) {
      return
    }

    // Workspace changed — update ref and re-fetch
    prevWorkspaceIdRef.current = currentWsId

    // Clear current conversation since it may belong to the old workspace
    setCurrentConversationId(null)
    localStorage.removeItem(LS_KEY)

    // Re-fetch conversations for the new workspace
    loadConversations()
  }, [activeWorkspace?.id, loadConversations, setCurrentConversationId])

  const selectConversation = useCallback(async (conversationId: number) => {
    setCurrentConversationId(conversationId)
    currentConversationIdRef.current = conversationId
    localStorage.setItem(LS_KEY, String(conversationId))

    // Restore the agent lock from the conversation list (available immediately)
    const convItem = conversations.find((c) => c.id === conversationId)
    if (convItem?.agent_definition_id != null) {
      setAgentDefinitionId(convItem.agent_definition_id)
    }

    // If this conversation is currently streaming, its messages are already in
    // the map (the background stream has been writing there). Skip the fetch.
    if (streamingConversationIds.has(conversationId)) {
      return
    }

    try {
      setIsLoadingMessages(true)
      const conversation = await getConversation(conversationId)

      // Also restore from full conversation in case it wasn't in the list
      if (conversation.agent_definition_id != null) {
        setAgentDefinitionId(conversation.agent_definition_id)
      }

      const loadedMessages: ChatMessage[] = conversation.messages
        .filter(msg => msg.content != null)
        .map(msg => ({
          sender: msg.role === 'user' ? 'user' : 'ai',
          text: msg.content || '',
          functionCalls: msg.function_calls,
          blocks: msg.content_blocks,
          agent: msg.agent_info,
          timestamp: msg.created_at,
        }))
      setMessagesForConversation(conversationId, () => loadedMessages)
    } catch (error) {
      console.error('Failed to load conversation:', error)
    } finally {
      setIsLoadingMessages(false)
    }
  }, [conversations, streamingConversationIds, setCurrentConversationId, setIsLoadingMessages, setMessagesForConversation])

  // Load conversations only once on app boot
  useEffect(() => {
    if (conversationsLoaded) return
    loadConversations().then(async (convos) => {
      // Restore in-progress conversations BEFORE calling selectConversation so
      // that selectConversation skips its DB fetch for any conversation already
      // marked as streaming (useChat.ts selectConversation line ~470).
      await restoreInProgressConversations(addStreamingConversation, setMessagesForConversation)

      const savedId = localStorage.getItem(LS_KEY)
      if (savedId && convos && convos.length > 0) {
        const id = parseInt(savedId, 10)
        if (convos.some((c) => c.id === id)) {
          selectConversation(id)
        }
      }
    })
  }, [conversationsLoaded, addStreamingConversation, loadConversations, selectConversation, setMessagesForConversation])

  const startNewConversation = useCallback(async () => {
    try {
      const conversation = await createConversation(undefined, workspaceId)
      setCurrentConversationId(conversation.id)
      currentConversationIdRef.current = conversation.id
      localStorage.setItem(LS_KEY, String(conversation.id))
      setMessagesForConversation(conversation.id, () => [])
      // Reset agent to suggested agent or null — new conversation unlocks the selector
      userSelectedAgentRef.current = false
      setAgentDefinitionId(suggestedAgentDefinitionId ?? null)
      await loadConversations()
      return conversation.id
    } catch (error) {
      console.error('Failed to create conversation:', error)
      return null
    }
  }, [loadConversations, setCurrentConversationId, setMessagesForConversation, workspaceId, suggestedAgentDefinitionId])

  const removeConversation = useCallback(async (conversationId: number) => {
    try {
      await deleteConversation(conversationId)
      useChatStore.getState().deleteConversation(conversationId)
      if (currentConversationId === conversationId) {
        localStorage.removeItem(LS_KEY)
      }
      await loadConversations()
    } catch (error) {
      console.error('Failed to delete conversation:', error)
    }
  }, [currentConversationId, loadConversations])

  const sendMessage = useCallback(async (text: string, attachments: AttachmentFile[] = []) => {
    if (!text.trim() && attachments.length === 0) return
    // Prevent double-send on the same conversation
    if (currentConversationId != null && streamingConversationIds.has(currentConversationId)) return

    let conversationId = currentConversationId
    const isFirstMessage = messages.length === 0

    if (!conversationId) {
      const newId = await startNewConversation()
      if (!newId) return
      conversationId = newId
    }

    // Build message attachments with persistent data URIs (not object URLs which get revoked)
    // Images will render as thumbnails; non-images show '[Attached: filename]' text
    const messageAttachments: MessageAttachment[] = attachments.map(a => ({
      dataUrl: `data:${a.mediaType};base64,${a.base64Data}`,
      name: a.name,
      isImage: a.isImage,
      mediaType: a.mediaType,
    }))

    // Only show '[Attached: ...]' text for non-image attachments
    const nonImageAttachments = attachments.filter(a => !a.isImage)
    let displayText = text
    if (nonImageAttachments.length > 0) {
      const names = nonImageAttachments.map(a => a.name).join(', ')
      displayText = text ? `${text}\n\n[Attached: ${names}]` : `[Attached: ${names}]`
    }

    const userMessage: ChatMessage = {
      sender: 'user',
      text: displayText,
      timestamp: new Date().toISOString(),
      attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
    }
    setMessagesForConversation(conversationId as number, (prev) => [...prev, userMessage])

    // 2. Append empty AI placeholder — global bus events will fill it in place
    setMessagesForConversation(conversationId as number, (prev) => [
      ...prev,
      { sender: 'ai', text: '', timestamp: new Date().toISOString() },
    ])

    const activeConversationId = conversationId as number

    // Mark this conversation as actively streaming.
    addStreamingConversation(activeConversationId)

    try {
      // Fire-and-forget: POST returns 202 with {source, source_id} immediately.
      // The response content arrives via the global SSE event bus.
      const attachmentPayload = attachments.map(a => ({
        filename: a.name,
        base64_data: a.base64Data,
        media_type: a.mediaType,
      }))

      await sendChatMessage({
        prompt: text,
        conversationId: activeConversationId,
        model,
        agentDefinitionId: agentDefinitionId ?? null,
        attachments: attachmentPayload,
        workspace: workspacePath,
      })

      if (isFirstMessage) {
        updateConversation(activeConversationId, text.slice(0, 60)).catch(() => {})
      }
    } catch (error) {
      // Fire-and-forget failed — remove streaming indicator and show error
      useChatStore.getState().removeStreamingConversation(activeConversationId)
      useChatStore.getState().removeFirstTokenReceived(activeConversationId)
      clearAccumulator(activeConversationId)

      // Special handling for agent mismatch (user tried to switch agents in active conversation)
      const errorMessage = error instanceof AgentMismatchError
        ? 'Cannot switch agents in an active conversation. Start a new chat to use a different agent.'
        : error instanceof Error
          ? error.message
          : 'Unknown error'

      setMessagesForConversation(activeConversationId, (prev) => {
        const updated = [...prev]
        const last = updated[updated.length - 1]
        if (last?.sender === 'ai') {
          updated[updated.length - 1] = {
            ...last,
            text: `Error: ${errorMessage}`,
          }
        }
        return updated
      })
    }
  }, [currentConversationId, messages, model, streamingConversationIds, startNewConversation, workspacePath, agentDefinitionId, addStreamingConversation, setMessagesForConversation])

  const stopStream = useCallback(() => {
    if (currentConversationId !== null) {
      cancelChat(currentConversationId).catch(() => {})
    }
  }, [currentConversationId])

  const renameConversation = useCallback(async (conversationId: number, title: string) => {
    const trimmed = title.trim()
    if (!trimmed) return
    try {
      await updateConversation(conversationId, trimmed)
      useChatStore.getState().updateConversationTitle(conversationId, trimmed)
    } catch (error) {
      console.error('Failed to rename conversation:', error)
    }
  }, [])

  // Agent is locked once the conversation has messages (first message sets the lock)
  const isAgentLocked = messages.length > 0

  return {
    messages,
    isLoading,
    isStreaming,
    sendMessage,
    stopStream,
    model,
    setModel,
    agentDefinitionId,
    setAgentDefinitionId: handleSetAgentDefinitionId,
    isAgentLocked,
    workspaces,
    workspaceId,
    setWorkspaceId,
    conversations,
    currentConversationId,
    isLoadingConversations,
    streamingConversationIds,
    selectConversation,
    startNewConversation,
    removeConversation,
    renameConversation,
  }
}
